import { Gift, ChevronRight, Wrench } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

/**
 * LiveRoomActionRail — the vertical rail on the right side of the stage.
 *
 * As of the "unify Live" refactor, this rail is intentionally minimal.
 * Only user-facing controls that must always be one tap away live here:
 *   - Surf-to-next-live (when there is one)
 *   - Send Gift (visible to everyone; hidden during active battle — the
 *     bottom-center BattleGiftCta becomes the primary CTA instead)
 *   - Creator Tools (single expandable button — see LiveRoomCreatorTools)
 *
 * Chat, Viewers, and A/V toggles live in their own dedicated components
 * mounted elsewhere in the shell (LiveChatOverlay, LiveRoomViewersDrawer,
 * LiveRoomAvControls). Wheel / Battle / Co-host / BRB / Poll / Goal / Paid
 * Live / Split / Extensions / Beauty / Filters / Custom Gifts all moved
 * into the Tools sheet — nothing is scattered across the video anymore.
 */
export default function LiveRoomActionRail({
  nextLive,
  onSurf,
  onOpenGift,
  onOpenTools,
  toolsBadge,
  toolsTitle = "Actions",
  hideGift,
  extra = null,
}) {
  return (
    <div
      className="absolute right-3 z-30 flex flex-col items-center gap-3"
      style={{ bottom: "max(5rem, calc(env(safe-area-inset-bottom) + 4.5rem))" }}
    >
      {nextLive && onSurf && (
        <button
          type="button"
          data-testid="live-surf-next"
          onClick={onSurf}
          className="group relative w-14 h-14 rounded-full bg-black/60 backdrop-blur-md border border-white/15 text-white flex items-center justify-center hover:bg-white/10 transition"
          title={`Next live: ${nextLive.host?.display_name}`}
        >
          <ChevronRight size={22} className="animate-pulse" />
          <div className="absolute right-16 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap bg-black/80 backdrop-blur-md border border-white/10 rounded-full pl-1.5 pr-4 py-1.5 flex items-center gap-2">
            <Avatar className="w-7 h-7 border border-white/15">
              <AvatarImage src={nextLive.host?.avatar_url} />
              <AvatarFallback className="bg-rose-600 text-white text-[10px]">
                {nextLive.host?.display_name?.[0]?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="text-left">
              <div className="text-[10px] uppercase tracking-widest text-zinc-400 leading-tight">Next live</div>
              <div className="text-xs text-white leading-tight truncate max-w-[120px]">
                {nextLive.host?.display_name}
              </div>
            </div>
          </div>
        </button>
      )}

      {extra}

      {onOpenGift && !hideGift ? (
        <button
          type="button"
          data-testid="live-gift-button"
          onClick={onOpenGift}
          className="w-14 h-14 rounded-full bg-yellow-400 hover:bg-yellow-300 text-black shadow-lg shadow-yellow-500/30 flex items-center justify-center"
          title="Send a gift"
        >
          <Gift size={22} />
        </button>
      ) : null}

      {onOpenTools && (
        <button
          type="button"
          data-testid="live-tools-button"
          onClick={onOpenTools}
          className="relative w-14 h-14 rounded-full bg-black/60 backdrop-blur-md border border-white/15 text-white flex items-center justify-center hover:bg-white/10 transition"
          title={toolsTitle}
          aria-label={toolsTitle}
        >
          <Wrench size={20} />
          {toolsBadge ? (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] flex items-center justify-center font-semibold"
              data-testid="live-tools-badge"
            >
              {toolsBadge}
            </span>
          ) : null}
        </button>
      )}
    </div>
  );
}
